import { segment } from '@actions/analytics';
import {
  subscriptionSubTypesObj,
  subscriptionSubTypes,
  evtNameCategoryMap,
  subscriptionTypesObj,
  entityContentType,
  subscriptionTypes as subscriptionTypesMap,
  subscriptionTypes,
  isCentreSubscription,
  getContentTypeName,
  PLATFORM
} from '@constants/index';
import { sendEvents } from '@events/sendAnalytics';
import { hasActiveSubscriptionToGoal } from '@models/Me';
import { businessPlatform } from '@utils/constants';
import { commas } from '@utils/textHelper';
import {
  PRE_APPROVED_LOAN_PAYMENT_GATEWAY,
  UPI_Intent,
  UPI_QR,
  paymentGatewayTypes,
  paymentMethodNames
} from './constants';
import {
  CHECKOUT_ORDER_CREATED,
  CHECKOUT_TRIAL_ORDER_CREATED,
  CHECKOUT_PRIME_ORDER_CREATED,
  PLAN_SELECTION_PAGE_LOADED
} from './constants/segment';

export const CASH_ORDER_STATUS = {
  INIT: 0,
  CANCELLED: 1,
  PAID: 2
};

const calculateMonthlyEMIAmount = (amount, months, rate) => {
  if (!rate) {
    return Math.ceil(amount / months);
  }
  const monthlyRate = rate / (12 * 100); // get monthly rate
  const calcVal = (1 + monthlyRate) ** months;
  return parseInt((amount * monthlyRate * calcVal) / (calcVal - 1), 10);
};

export const getTotalDiscountText = (referral, currency) => {
  if (referral.message && referral.message.length > 0) {
    return referral.message;
  }
  if (referral.discount_type === 1 && referral.discount_value > 0) {
    return `Awesome! You got ${currency}${commas(referral.discount_value)} off`;
  }
  if (referral.discount_type === 2 && referral.discount_percent > 0) {
    return `Awesome! You got ${referral.discount_percent}% off`;
  }
  return null;
};

const daysRemainingInSubscription = (subscriptions, goalId) => {
  const currentSubscription = subscriptions?.find(
    (subscription) => subscription.subscription.value.uid === goalId
  );
  return currentSubscription?.time_remaining / (3600 * 24);
};

export const getPaymentMethod = (paymentMethods, type) => {
  return paymentMethods.find(
    ({ metaInfo: { paymentTypeId } }) => paymentTypeId === type
  );
};

export const EMI_TYPE_LABEL_MAPPING = {
  'cardless-emi': 'Cardless',
  'debit-card': 'Debit card',
  'credit-card': 'Credit card'
};

export const JUSPAY_PAYMENT_STATUS = {
  success: 21,
  failure: [26, 27, 22]
};

export const isNoCostEMIAvailable = (
  selectedEMIPlan,
  noCostEmiMinimumSubscriptionDuration,
  totalAmount,
  subscriptionDuration
) => {
  return (
    selectedEMIPlan.noCostEmiAvailable &&
    selectedEMIPlan.noCostEmiMinAmount / 100 <= parseInt(totalAmount, 10) &&
    subscriptionDuration >= noCostEmiMinimumSubscriptionDuration
  );
};

function getAmount(data) {
  return Math.round(data?.reduce((acc, _val) => acc + _val.amount, 0) ?? 0);
}

export const plusProceedPaymentEventAnalytics = (info) => {
  const {
    referral_code,
    goal_uid,
    price,
    subscription_type,
    subscription_duration,
    subscription_price,
    userId,
    username,
    name: goalName,
    user_auth
  } = info;
  const eventData = {
    referral_code: referral_code ?? 'NA',
    goal_uid,
    'Has Referral Code': !!referral_code,
    platform: PLATFORM.MOBILE,
    USERNAME: username,
    event: 'Plus: Proceed Payment',
    'User Id': userId,
    Amount: price ?? subscription_price,

    subscription_duration,

    env: 'production',
    'Last Primary Source': 'Home',
    'Current Screen': 'Subscription Plan Selection',
    'Learner Username': username,
    'Goal Name': goalName,

    learner_uid: userId,
    user_uid: userId,
    subscription_type: subscriptionTypes[subscription_type?.toUpperCase()],

    'User Auth': user_auth
  };

  segment.track(PLAN_SELECTION_PAGE_LOADED, eventData);
};

export const filterOfflineSupportedPaymentMethods = (data) => {
  const modifyData = [...data];
  modifyData?.forEach((indexValues) => {
    indexValues?.data?.forEach((dataVal, index) => {
      if (dataVal?.metaInfo?.paymentTypeName === 'Debit / Credit Card') {
        const filteredData = dataVal?.data?.filter((valData) => {
          return valData?.supportedReferenceIds.includes('Offline');
        });

        const indexValuesData = [...indexValues?.data];
        indexValuesData[index].data = [...filteredData];
      }
      if (dataVal?.metaInfo?.paymentTypeName === 'Netbanking') {
        dataVal?.data?.forEach((banksData) => {
          const filteredData = banksData?.banks?.filter((bank) => {
            return bank?.supportedReferenceIds?.includes('Offline');
          });
          // eslint-disable-next-line no-param-reassign
          banksData.banks = [...filteredData];
        });
      }
    });
  });

  return modifyData;
};

export const orderCreatedAnalytics = (info) => {
  const {
    creditsApplied,
    referralCode,
    paymentMethod,
    paymentMethodType,
    isPartial = false,
    parts = null,
    subscription = {},
    me = {},
    selectedGoal = {},
    data,
    pipIndex,
    paymentDetails = [],
    isCombo,
    comboId,
    comboName,
    referralError,
    selectedMethod,
    purchaseType,
    emiData,
    gateway,
    cardType,
    loanBank
  } = info;

  const eventData = {
    name: 'Plus: Order Created',
    Amount: isPartial
      ? getAmount(paymentDetails)
      : Math.round(data.amount / 100),
    'Partial Amount': isPartial
      ? Math.round(paymentDetails[pipIndex - 1]?.amount ?? 0)
      : Math.round(data.amount / 100 ?? 0),
    'Has Referral Code': !!referralCode,
    'Referral Code': referralCode,
    'Has Applied Credits': !!creditsApplied,
    'Subscription Duration': subscription.duration,
    'Payment Gateway': paymentMethod,
    'Subscription Type': subscription?.type,
    'Is Part Payment': isPartial,
    'Opted Part Payments': parts,
    'Order Id': data.order_id,
    'Goal Id': selectedGoal?.uid,
    'Goal Name': selectedGoal?.name,
    'Learner Id': me?.uid,
    'Learner Username': me?.username,
    'Is Combo': isCombo
  };
  if (isCombo) {
    eventData['Combo Id'] = comboId;
    eventData['Combo Name'] = comboName;
  }
  if (isPartial) {
    eventData['Current Part Payment Index'] = pipIndex;
  }
  sendEvents('Plus: Order Created', eventData);

  const selectedPaymentMethod = paymentMethodNames?.find(
    (paymentMethodName) => paymentMethodName.type === selectedMethod
  );

  const segmentEventData = {
    purchase_type: purchaseType,
    // Adding new prop here for upgrade flow
    subscription_type: isCentreSubscription(
      subscription.type,
      subscription.contentTypeId
    )
      ? 'Offline'
      : subscriptionTypesObj[subscription.type],
    goal_uid: comboId,
    goal_name: comboName,
    subscription_duration: subscription?.duration,
    price: isPartial
      ? getAmount(paymentDetails)
      : Math.round(data.amount / 100),
    referral_code: referralCode || 'NA',
    is_referral_successful: Boolean(referralCode) && !referralError,
    has_applied_credits: creditsApplied,
    credits_applied:
      (creditsApplied && subscription?.redeemableCreditAmount) || 0,
    is_partpayment: isPartial,
    payment_method_type: paymentMethodType,
    payment_method:
      paymentMethod === UPI_QR ||
      paymentMethod === UPI_Intent ||
      paymentMethod === PRE_APPROVED_LOAN_PAYMENT_GATEWAY
        ? paymentMethod
        : cardType || selectedPaymentMethod?.payment_method,
    loan_bank: loanBank,
    payment_gateway: paymentGatewayTypes[gateway],
    is_combo: isCombo,
    is_upgrade:
      hasActiveSubscriptionToGoal(comboId) &&
      subscription.type === subscriptionTypesMap.ICONIC,
    is_emi: !!emiData,
    order_id: data.orderId,
    subscription_price: Number(subscription.price),
    // eslint-disable-next-line no-nested-ternary
    entity_content_type: isCentreSubscription(
      subscription.type,
      subscription.contentTypeId
    )
      ? getContentTypeName(subscription?.contentTypeId)
      : subscription.type === subscriptionTypesMap.PLATFORM
      ? entityContentType.PLATFORM_BATCH_GROUP
      : entityContentType.TOPOLOGY,
    entity_uid: subscription?.uid,
    entity_title: subscription?.title,
    subscription_sub_type:
      subscriptionSubTypesObj[subscriptionSubTypes.STANDARD]
  };

  if (
    subscription.type === subscriptionTypes.CENTRE ||
    isCentreSubscription(subscription?.type, subscription?.contentTypeId)
  ) {
    segmentEventData.business_platform = businessPlatform.UA_OFFLINE;
  }
  const isPrimeSubscription = subscription?.contentTypeId === 507;
  segment.track(
    // eslint-disable-next-line no-nested-ternary
    subscription?.type === subscriptionTypes.TRIAL
      ? CHECKOUT_TRIAL_ORDER_CREATED
      : isPrimeSubscription
      ? CHECKOUT_PRIME_ORDER_CREATED
      : CHECKOUT_ORDER_CREATED,
    {
      ...segmentEventData,
      entity_content_type: isPrimeSubscription
        ? 'Prime'
        : segmentEventData?.entity_content_type
    },
    evtNameCategoryMap.BOTH
  );
};

const utils = {
  calculateMonthlyEMIAmount,
  daysRemainingInSubscription,
  isNoCostEMIAvailable,
  getTotalDiscountText,
  getPaymentMethod,
  filterOfflineSupportedPaymentMethods
};

export default utils;
